import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantData } from '../context/TenantDataContext';
import { useAuth } from '../context/AuthContext';
import { useItList } from '../hooks/useItList';
import { useResizablePanel } from '../hooks/useResizablePanel';
import { buildItMailto } from '../utils/supportMail';
import TrailFrame, { TrailPageLoading } from '../components/TrailFrame';
import ConfirmDialog from '../components/ConfirmDialog';
import './styles.css';
import './CompliancePage.css';
import './ItListPage.css';

const Svg = ({ d, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const P = {
  user: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-3.5 3.2-5.5 7-5.5s7 2 7 5.5" /></>,
  pc: <><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></>,
  app: <><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>,
  send: <path d="m22 2-11 11M22 2l-7 20-4-9-9-4Z" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  list: <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />,
  go: <path d="M9 6l6 6-6 6" />,
  doc: <><path d="M7 3h8l4 4v14H7z" /><path d="M15 3v4h4M10 12h6M10 16h6" /></>,
  check: <path d="M5 12l5 5L20 7" />,
};
const AREA_ICON = { Brugere: P.user, Enheder: P.pc, Apps: P.app, 'E-mail': P.mail };
const dateText = (iso) => new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
const SUPPORT = 'support@nordcomply.dk';

function Point({ item, sent, checked, canPick, onToggle, onRemoveItem, onRemoveObject, onBack }) {
  const tone = sent ? 'ok' : item.tone === 'bad' ? 'bad' : 'warn';
  const objects = item.objects || [];
  const isDecision = item.key.startsWith('dec:');
  const meta = [item.area, objects.length > 0 ? `${objects.length} valgt` : (item.count ? `${item.count} ${item.tone === 'bad' ? 'kræver handling' : 'bør gennemgås'}` : null)].filter(Boolean).join(' · ');
  return (
    <article className={`ip-pt${sent ? ' ip-pt--sent' : ''}${!sent && checked ? ' is-on' : ''}`}>
      <div className="ip-pt__h">
        {!sent && canPick && (
          <label className="wg__pick" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={checked} onChange={() => onToggle(item.key)} aria-label={`Send ${item.title} til IT`} />
            <span className="wg__cb" aria-hidden="true"><Svg d={P.check} size={11} /></span>
          </label>
        )}
        <span className={`ip-ic ip-ic--${tone}`}><Svg d={isDecision ? P.doc : (AREA_ICON[item.area] || P.list)} size={17} /></span>
        <div className="ip-pt__t"><b>{item.title}</b><small>{meta}</small></div>
        {sent && <span className="ip-st">Mailudkast åbnet {dateText(item.sentAt)}</span>}
        {sent && <button type="button" className="ip-back" onClick={() => onBack(item.key)} title="Læg tilbage på listen over det, der skal sendes">Send igen</button>}
        <button type="button" className="ip-x" onClick={() => onRemoveItem(item)} title="Fjern hele punktet" aria-label={`Fjern ${item.title} fra listen`}><Svg d={P.x} /></button>
      </div>
      <div className="ip-body">
        {item.detail && <p className="ip-quote">{item.detail}</p>}
        {objects.length > 0 && (
          <div className="ip-chs">
            {objects.map((o) => (
              <span className="ip-ch" key={o.id}>{o.name}<button type="button" onClick={() => onRemoveObject(item, o)} title="Fjern fra listen" aria-label={`Fjern ${o.name} fra listen`}><Svg d={P.x} size={10} /></button></span>
            ))}
          </div>
        )}
        {objects.some((o) => o.note) && (
          <ul className="ip-nt">
            {objects.filter((o) => o.note).map((o) => <li key={o.id}><b>{o.name}</b> <em>{o.note}</em></li>)}
          </ul>
        )}
      </div>
    </article>
  );
}

// "IT-opgaver": what the customer has collected from Gennemgang to send to its IT partner. The system does not send anything itself:
// it makes a mail draft that opens in the person's own mail program, ready to send, so it can never know whether the mail was sent.
// Therefore the page asks, and the person removes what they are done with. Only somebody who may take decisions can send.
// Same frame as Beslutninger and Historik: the switch in a card, the list under it, the sending in the side panel.
export default function ItListPage() {
  const { tenant, loading } = useTenantData();
  const { canDecide } = useAuth();
  const navigate = useNavigate();
  const detailPanel = useResizablePanel(600, 340, 1000);
  const { waiting, sent, ready, remove, markSent, unmarkSent } = useItList(tenant?.tenantId);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [off, setOff] = useState(() => new Set()); // keys that are left out of the mail; everything else is sent
  const [confirm, setConfirm] = useState(null); // what the person is about to take off the list, until they say yes
  const [asking, setAsking] = useState([]); // keys of the mail draft that was just opened, waiting for "was it sent?"
  if (loading || !tenant) return <TrailPageLoading current="it-list" text="Det, I vil have IT til at hjælpe med. Saml punkterne her, og send dem samlet." panelWidth={detailPanel.width} />;

  const to = tenant.supportEmail || '';
  const who = tenant.supportName || 'IT';
  const fail = (e) => setError(e.message || 'Kunne ikke opdatere IT-opgaver');
  const chosen = waiting.filter((i) => !off.has(i.key));
  const toggle = (key) => setOff((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const allOn = waiting.length > 0 && chosen.length === waiting.length;
  const toggleAll = () => setOff(allOn ? new Set(waiting.map((i) => i.key)) : new Set());
  const send = async () => {
    setError('');
    const keys = chosen.map((i) => i.key);
    window.location.href = buildItMailto(chosen, tenant.name, to, message);
    try { await markSent(keys); setMessage(''); setAsking(keys); } catch (e) { fail(e); }
  };
  const answerNo = () => { const keys = asking; setAsking([]); unmarkSent(keys).catch(fail); };
  const removeItem = (item) => setConfirm({ key: item.key, title: item.title });
  const removeObject = (item, o) => setConfirm({ key: item.key, title: item.title, objectId: o.id, objectName: o.name });
  const confirmRemove = () => {
    const c = confirm;
    setConfirm(null);
    (c.objectId ? remove(c.key, [c.objectId]) : remove(c.key)).catch(fail);
  };
  const back = (key) => unmarkSent([key]).catch(fail);
  const empty = ready && waiting.length === 0 && sent.length === 0;
  const asked = asking.filter((k) => sent.some((i) => i.key === k));

  return (
    <div className="cmp dc-page ip-page">
      <div className="head tf-head">
        <div>
          <div className="eye">Dokumentation</div>
          <h1>Revisionsspor</h1>
          <p>Det, I vil have IT til at hjælpe med. Saml punkterne her, og send dem samlet.</p>
        </div>
      </div>
      <TrailFrame
        current="it-list"
        panelWidth={detailPanel.width}
      >
      <div className="workspace">
        <div className="list-panel list-panel--float wq-menu">
          <div className="wq-tablecard ip-list">
            {waiting.length > 0 && (
              <div className="ip-selbar">
                <label className="wg__pick">
                  <input type="checkbox" checked={allOn} onChange={toggleAll} aria-label="Vælg alle" />
                  <span className="wg__cb" aria-hidden="true"><Svg d={P.check} size={11} /></span>
                </label>
                <b>Vælg alle</b>
                <span>{chosen.length} af {waiting.length} valgt</span>
              </div>
            )}
            {empty ? (
              <div className="ip-empty">
                <span className="ip-empty__i"><Svg d={P.list} size={26} /></span>
                <b>Intet på listen endnu</b>
                <p>I Gennemgang kan I vælge et eller flere objekter i et fund og trykke <i>Tilføj til IT-opgaver</i>. De samles her, og I sender dem i én mail.</p>
                <button type="button" className="ip-go" onClick={() => navigate(`/tenant/${tenant.tenantId}/work-queue`)}>Åbn Gennemgang <Svg d={P.go} size={14} /></button>
              </div>
            ) : (
              <>
                <h2 className="ip-sec">Klar til at sende <span>{waiting.length}</span></h2>
                {waiting.length === 0
                  ? <p className="ip-none">Intet venter på at blive sendt.</p>
                  : waiting.map((i) => <Point key={i.key} item={i} checked={!off.has(i.key)} canPick onToggle={toggle} onRemoveItem={removeItem} onRemoveObject={removeObject} />)}
                {sent.length > 0 && (
                  <>
                    <h2 className="ip-sec">Sendt til IT <span>{sent.length}</span></h2>
                    <p className="ip-sub">Systemet kan ikke se, om mailen er sendt. Fjern punkterne, når I er færdige med dem.</p>
                    {sent.map((i) => <Point key={i.key} item={i} sent onRemoveItem={removeItem} onRemoveObject={removeObject} onBack={back} />)}
                  </>
                )}
              </>
            )}
            {error && <p className="ip-err">{error}</p>}
          </div>
        </div>
        <div className={`resize-handle${detailPanel.dragging ? ' is-dragging' : ''}`} onMouseDown={detailPanel.onMouseDown} />
        <div className="detail-panel-col" style={{ width: detailPanel.width }}>
          <div className="detail-panel">
            <div className="ip-pn">
              {asked.length > 0 ? (
                <>
                  <h3>Blev mailen sendt?</h3>
                  <p className="ip-note ip-note--top">Systemet kan ikke se, om mailen blev sendt fra jeres mailprogram. Fortæl os det, så holder listen styr på det.</p>
                  <button type="button" className="ip-send" onClick={() => setAsking([])}><Svg d={P.check} />Ja, den blev sendt <em>{asked.length}</em></button>
                  <button type="button" className="ip-no" onClick={answerNo}>Nej, læg tilbage på listen</button>
                </>
              ) : (
                <>
                  <h3>Send til {who}</h3>
                  <p className="ip-to"><span>Til</span><b>{to || 'Ingen modtager gemt, skriv den i mailen'}</b></p>
                  <label className="ip-m">
                    <span>Besked til {who} <small>(valgfri)</small></span>
                    <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value.slice(0, 1000))} placeholder="Skriv en besked til IT, hvis I vil tilføje noget" />
                  </label>
                  <button type="button" className="ip-send" onClick={send} disabled={chosen.length === 0 || !canDecide} title={canDecide ? '' : 'Du har ikke ret til at sende fra listen'}>
                    <Svg d={P.send} />Send til {who} {chosen.length > 0 && <em>{chosen.length}</em>}
                  </button>
                  <p className="ip-note">Kun de afkrydsede punkter kommer med i mailen.</p>
                </>
              )}
              <div className="ip-warn">
                <b>Systemet sender ikke selv mailen</b>
                <p>Det laver et mailudkast, som åbnes i dit mailprogram, klar til afsendelse. Ønsker I, at systemet selv skal sende, så skriv til <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>, eller brug Feedback i sidemenuen.</p>
              </div>
              <div className="ip-how">
                <b>Sådan virker det</b>
                <ol>
                  <li>Tilføj fund fra Gennemgang med <i>Tilføj til IT-opgaver</i>.</li>
                  <li>Afkryds det, der skal med, og send.</li>
                  <li>Punkter I er færdige med, fjerner I selv. Løste fund forsvinder af sig selv.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
      </TrailFrame>
      {confirm && (
        <ConfirmDialog
          title={confirm.objectId ? 'Fjern fra IT-opgaver?' : 'Fjern punkt fra IT-opgaver?'}
          message={confirm.objectId ? `"${confirm.objectName}" fjernes fra "${confirm.title}" på IT-opgaver. Selve fundet i Gennemgang ændres ikke.` : `"${confirm.title}" fjernes fra IT-opgaver med alle objekter. Selve fundet i Gennemgang ændres ikke.`}
          confirmLabel="Fjern"
          onCancel={() => setConfirm(null)}
          onConfirm={confirmRemove}
        />
      )}
    </div>
  );
}
